import React, { useState, useEffect } from "react";
import QRScanner from "./components/QRScanner";
import "./App.css";

const SCAN_LOCATIONS = {
  entrance: "Entrance",
  food: "Food Corner",
  breakout: "Breakout Rooms",
};

const STORAGE_KEY = "event-scanner-location";

function App() {
  const [scanResult, setScanResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [scanLocation, setScanLocation] = useState(() => {
    // Initialize from localStorage or default to "entrance"
    return localStorage.getItem(STORAGE_KEY) || "entrance";
  });

  // Update localStorage when location changes
  useEffect(() => {
    if (scanLocation !== localStorage.getItem(STORAGE_KEY)) {
      localStorage.setItem(STORAGE_KEY, scanLocation);
      window.location.reload();
    }
  }, [scanLocation]);

  const handleScan = async (barcode) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        "https://vsl7ng0fsf.execute-api.eu-central-1.amazonaws.com/event-tickets/scan",
        // "http://localhost:8003/event-tickets/scan",
        {
          method: "POST",
          body: JSON.stringify({
            barcode,
            location: scanLocation,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        // 200 status code
        setScanResult({
          isValid: true,
          message: `${data.attendee.firstname} ${data.attendee.lastname}`,
          details: {
            ticket: data.order_item.ticket_sale_name,
            bookingNo: data.order_item.booking_no,
            orderedAt: data.order_item.created_at,
            orderedBy:
              data.order_item.customer_firstname +
              " " +
              data.order_item.customer_lastname,
            customerEmail: data.order_item.customer_email,
          },
        });
      } else if (response.status === 404) {
        // Ticket not found
        setScanResult({
          isValid: false,
          message: data.message || "Ticket not found",
        });
      } else if (response.status === 409) {
        // Ticket already used
        setScanResult({
          isValid: false,
          message: (
            <>
              {data.message || "Ticket already used"}
              <br />
              {`${data.attendee.firstname} ${data.attendee.lastname}`}
            </>
          ),
          details: {
            ticket: data.order_item.ticket_sale_name,
            bookingNo: data.order_item.booking_no,
            orderedAt: data.order_item.created_at,
            orderedBy:
              data.order_item.customer_firstname +
              " " +
              data.order_item.customer_lastname,
            customerEmail: data.order_item.customer_email,
          },
        });
      } else {
        // Other errors
        setScanResult({
          isValid: false,
          message: data.message || "Invalid ticket",
        });
      }
    } catch (error) {
      setScanResult({
        isValid: false,
        message: "Error scanning ticket",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString("nl-NL", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Event Ticket Scanner</h1>
        <div className="location-selector">
          <label htmlFor="scan-location">Scan Location:</label>
          <select
            id="scan-location"
            value={scanLocation}
            onChange={(e) => setScanLocation(e.target.value)}
          >
            {Object.entries(SCAN_LOCATIONS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </header>
      <main>
        <QRScanner onScan={handleScan} />
        {isLoading && <div className="loading">Validating ticket...</div>}
        {scanResult && (
          <div className={`result ${scanResult.isValid ? "valid" : "invalid"}`}>
            <div className="result-message">{scanResult.message}</div>
            {scanResult.details && (
              <div className="result-details">
                <div className="detail-item">
                  <strong>Ticket:</strong> {scanResult.details.ticket}
                </div>
                <div className="detail-item">
                  <strong>Booking No:</strong> {scanResult.details.bookingNo}
                </div>
                <div className="detail-item">
                  <strong>Ordered At:</strong>{" "}
                  {formatDate(scanResult.details.orderedAt)}
                </div>
                <div className="detail-item">
                  <strong>Ordered By:</strong> {scanResult.details.orderedBy}
                </div>
                <div className="detail-item">
                  <strong>Email:</strong> {scanResult.details.customerEmail}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
